import React from 'react';
import PharmacyLogo from './PharmacyLogo';
import { formatPakistanDateTime } from '../utils/pakistanTime';

export default function ThermalReceipt({ data }) {
  if (!data) return null;

  const {
    invoice,
    date = new Date(),
    items = [],
    subtotal = 0,
    discount = 0,
    discountType = 'fixed',
    discountRate = null,
    taxRate = 0,
    taxAmount = 0,
    grandTotal = 0,
    paidAmount = 0,
    paymentMethod = 'cash',
    change = 0,
    customer = null,
    customerBalance = 0,
    advanceDeducted = 0,
    creditDuesAdded = 0,
    projectedBalance = 0,
    cashier = 'Admin',
    settings = {},
  } = data;

  const pharmacyName = settings.pharmacy_name || 'Gaza Pharmacy';
  const pharmacyAddress = settings.pharmacy_address || 'Gaza City, Main Street';
  const pharmacyPhone = settings.pharmacy_phone || '0599123456';
  const pharmacyEmail = settings.pharmacy_email || '';
  const receiptFooter = settings.receipt_footer || 'Thank you for your purchase!';

  const FONT_MAP = {
    consolas: "'Consolas', 'Lucida Console', 'Liberation Mono', 'Courier Prime', monospace",
    segoe: "'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
    'roboto-mono': "'Roboto Mono', 'Space Mono', 'Consolas', monospace",
    lucida: "'Lucida Console', 'Consolas', monospace",
    courier: "'Courier New', Courier, monospace",
  };

  const selectedFontKey = settings.receipt_font || 'consolas';
  const fontFamily = FONT_MAP[selectedFontKey] || FONT_MAP.consolas;
  const isDarkWeight = settings.receipt_font_weight !== 'normal';

  const formattedDate = formatPakistanDateTime(date, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const hasAdvance = customerBalance < 0;
  const newHasAdvance = projectedBalance < 0;
  const newHasDues = projectedBalance > 0;

  return (
    <div
      className={`w-full max-w-[68mm] mx-auto bg-white p-2 print:p-0 print:pr-1.5 print:m-0 text-black text-[11px] leading-tight select-none box-border ${
        isDarkWeight ? 'font-semibold' : 'font-normal'
      }`}
      style={{
        fontFamily,
        WebkitFontSmoothing: 'antialiased',
        textRendering: 'geometricPrecision',
      }}
    >
      {/* COMPANY HEADER */}
      <div className="text-center space-y-0.5">
        <PharmacyLogo
          logo={settings.pharmacy_logo}
          alt={`${pharmacyName} logo`}
          className="mx-auto h-12 w-12 rounded object-contain filter grayscale contrast-150"
          imageClassName="mx-auto h-12 w-12 rounded object-contain filter grayscale contrast-150"
        />
        <h1 className="text-sm font-black uppercase tracking-wider text-black pt-0.5">{pharmacyName}</h1>
        {pharmacyAddress && <p className="text-[10.5px] font-medium text-black leading-tight">{pharmacyAddress}</p>}
        {pharmacyPhone && <p className="text-[10.5px] font-bold text-black leading-tight">Tel: {pharmacyPhone}</p>}
        {pharmacyEmail && <p className="text-[10px] font-medium text-black leading-tight">{pharmacyEmail}</p>}
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      {/* INVOICE META */}
      <div className="space-y-0.5 text-[11px] text-black">
        <div className="flex justify-between">
          <span className="font-bold">INVOICE:</span>
          <span className="font-bold pr-0.5">{invoice}</span>
        </div>
        <div className="flex justify-between">
          <span>DATE:</span>
          <span className="font-semibold pr-0.5">{formattedDate}</span>
        </div>
        <div className="flex justify-between">
          <span>CASHIER:</span>
          <span className="font-semibold pr-0.5">
            {cashier} {data.cashier_role ? `(${data.cashier_role.toUpperCase()})` : ''}
          </span>
        </div>
        <div className="flex justify-between">
          <span>CUSTOMER:</span>
          <span className="font-bold max-w-[130px] truncate pr-0.5">{customer?.name || 'Walk-in Customer'}</span>
        </div>
        {customer?.phone && (
          <div className="flex justify-between">
            <span>PHONE:</span>
            <span className="font-semibold pr-0.5">{customer.phone}</span>
          </div>
        )}
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      {/* ITEMS TABLE */}
      <div>
        <div className="flex justify-between font-bold text-[11px] border-b-2 border-black pb-1 mb-1 text-black">
          <span className="w-[44%]">ITEM</span>
          <span className="w-[14%] text-center">QTY</span>
          <span className="w-[20%] text-right">PRICE</span>
          <span className="w-[22%] text-right pr-0.5">TOTAL</span>
        </div>

        <div className="space-y-1.5">
          {items.map((item, idx) => {
            const qty = parseInt(item.unit_quantity || item.quantity, 10) || 0;
            const unitName = item.selected_unit || item.unit_name || '';
            const price = parseFloat(item.unit_price) || 0;
            const total = qty * price;
            const itemName = item.name || item.medicine_name || `Medicine #${item.medicine_id || idx + 1}`;
            return (
              <div key={idx} className="flex justify-between items-start text-[11px] leading-tight text-black">
                <div className="w-[44%] pr-1 font-semibold break-words">
                  <p className="font-bold text-black">{itemName}</p>
                  {unitName && (
                    <p className="text-[10px] text-black font-medium">({unitName})</p>
                  )}
                </div>
                <span className="w-[14%] text-center font-bold">{qty}</span>
                <span className="w-[20%] text-right font-medium">{price.toFixed(2)}</span>
                <span className="w-[22%] text-right font-bold pr-0.5">{total.toFixed(2)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      {/* BILL TOTALS */}
      <div className="space-y-1 text-[11px] text-black">
        <div className="flex justify-between font-medium">
          <span>SUB-TOTAL:</span>
          <span className="pr-0.5">Rs. {parseFloat(subtotal).toFixed(2)}</span>
        </div>

        {parseFloat(discount) > 0 && (
          <div className="flex justify-between font-semibold">
            <span>DISCOUNT{discountType === 'percent' && discountRate ? ` (${discountRate}%)` : ''}:</span>
            <span className="pr-0.5">- Rs. {parseFloat(discount).toFixed(2)}</span>
          </div>
        )}

        {parseFloat(taxRate) > 0 && (
          <div className="flex justify-between font-semibold">
            <span>TAX ({parseFloat(taxRate)}%):</span>
            <span className="pr-0.5">+ Rs. {parseFloat(taxAmount).toFixed(2)}</span>
          </div>
        )}

        <div className="flex justify-between text-xs font-black border-t-2 border-b-2 border-black py-1 my-1 text-black">
          <span>NET TOTAL:</span>
          <span className="pr-0.5">Rs. {parseFloat(grandTotal).toFixed(2)}</span>
        </div>

        <div className="flex justify-between font-medium">
          <span>PAYMENT METHOD:</span>
          <span className="font-bold uppercase pr-0.5">{paymentMethod}</span>
        </div>

        {paymentMethod !== 'credit' && (
          <>
            <div className="flex justify-between font-medium">
              <span>CASH RECEIVED:</span>
              <span className="font-semibold pr-0.5">Rs. {parseFloat(paidAmount).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-black">
              <span>CASHBACK:</span>
              <span className="pr-0.5">Rs. {Math.max(0, parseFloat(change)).toFixed(2)}</span>
            </div>
          </>
        )}
      </div>

      {/* CUSTOMER LEDGER BREAKDOWN */}
      {customer && (paymentMethod === 'credit' || Math.abs(customerBalance) > 0) && (
        <>
          <div className="border-t-2 border-dashed border-black my-2" />
          <div className="space-y-0.5 text-[10.5px] text-black">
            <p className="font-bold text-center uppercase tracking-wider mb-1 text-black">--- Customer Ledger ---</p>
            <div className="flex justify-between font-medium">
              <span>PREV BALANCE:</span>
              <span className="font-semibold">
                Rs. {Math.abs(customerBalance).toFixed(2)}{' '}
                {hasAdvance ? '(Adv)' : customerBalance > 0 ? '(Due)' : ''}
              </span>
            </div>
            {hasAdvance && advanceDeducted > 0 && (
              <div className="flex justify-between font-semibold">
                <span>DEDUCTED FROM ADV:</span>
                <span>- Rs. {parseFloat(advanceDeducted).toFixed(2)}</span>
              </div>
            )}
            {creditDuesAdded > 0 && (
              <div className="flex justify-between font-bold">
                <span>ADDED TO DUES:</span>
                <span>+ Rs. {parseFloat(creditDuesAdded).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t-2 border-dotted border-black pt-0.5 mt-0.5 text-black">
              <span>NEW BALANCE:</span>
              <span>
                Rs. {Math.abs(projectedBalance).toFixed(2)}{' '}
                {newHasAdvance ? '(Remaining Adv)' : newHasDues ? '(Total Due)' : '(Settled)'}
              </span>
            </div>
          </div>
        </>
      )}

      <div className="border-t-2 border-dashed border-black my-2" />

      {/* FOOTER */}
      <div className="text-center space-y-1 text-[10.5px] text-black">
        <p className="font-bold whitespace-pre-line text-black">{receiptFooter}</p>
        <p className="text-[10px] font-bold text-black">POS: Gaza Pharmacy Management System</p>
        <p className="text-[9.5px] font-medium text-black">Developed by Jahasoft (https://jahasoft.pk)</p>
        <p className="tracking-widest font-black text-[10px] text-black pt-0.5">*** {invoice} ***</p>
      </div>
    </div>
  );
}
